// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Header, Button, Form, Message, Icon, Segment, Tab, Grid } from 'semantic-ui-react';
import { emailTemplateApi, EmailTemplate, RenderedEmail } from '../api/emailTemplateApi';

function EmailTemplatePreviewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [template, setTemplate] = useState<EmailTemplate | null>(null);
  const [preview, setPreview] = useState<RenderedEmail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Test variables for preview
  const [testVariables, setTestVariables] = useState<Record<string, string>>({
    clientName: 'John Doe',
    profileName: 'Doe Family Trust',
    advisorName: 'Jane Smith',
    brokerageName: 'ABC Financial Services',
    providerName: 'Canada Life Insurance',
    policyNumber: 'POL-123456',
    policyType: 'Critical Illness Insurance',
    signedCount: '2',
    totalSignatories: '3',
    signingUrl: 'https://example.com/sign/abc123',
    completedDate: new Date().toLocaleDateString()
  });

  const loadTemplate = useCallback(async (templateId: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await emailTemplateApi.getById(templateId);
      setTemplate(data);
      // Automatically generate preview with default test values
      await generatePreview(templateId, testVariables);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load template');
    } finally {
      setLoading(false);
    }
  }, [testVariables]);

  useEffect(() => {
    if (id) {
      loadTemplate(id);
    }
  }, [id, loadTemplate]);

  const generatePreview = async (templateId: string, variables: Record<string, string>) => {
    try {
      setLoading(true);
      setError(null);
      const rendered = await emailTemplateApi.preview(templateId, variables);
      setPreview(rendered);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate preview');
    } finally {
      setLoading(false);
    }
  };

  const handleVariableChange = (key: string, value: string) => {
    setTestVariables({ ...testVariables, [key]: value });
  };

  const handleRefreshPreview = () => {
    if (id) {
      generatePreview(id, testVariables);
    }
  };

  const panes = [
    {
      menuItem: { key: 'html', icon: 'html5', content: 'HTML Preview' },
      render: () => (
        <Tab.Pane>
          {preview && (
            <div style={{ border: '1px solid #ddd', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{ background: '#f5f5f5', padding: '10px', borderBottom: '1px solid #ddd' }}>
                <strong>Subject:</strong> {preview.subject}
              </div>
              <iframe
                title='HTML Preview'
                srcDoc={preview.bodyHtml}
                style={{
                  width: '100%',
                  minHeight: '500px',
                  border: 'none',
                  background: 'white'
                }}
              />
            </div>
          )}
        </Tab.Pane>
      )
    },
    {
      menuItem: { key: 'text', icon: 'file text', content: 'Plain Text Preview' },
      render: () => (
        <Tab.Pane>
          {preview && (
            <div style={{ border: '1px solid #ddd', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{ background: '#f5f5f5', padding: '10px', borderBottom: '1px solid #ddd' }}>
                <strong>Subject:</strong> {preview.subject}
              </div>
              <pre style={{
                padding: '15px',
                whiteSpace: 'pre-wrap',
                fontFamily: 'monospace',
                fontSize: '0.9em',
                margin: 0,
                background: 'white'
              }}>
                {preview.bodyText || 'No plain text version available'}
              </pre>
            </div>
          )}
        </Tab.Pane>
      )
    },
    {
      menuItem: { key: 'source', icon: 'code', content: 'HTML Source' },
      render: () => (
        <Tab.Pane>
          {preview && (
            <div style={{ border: '1px solid #ddd', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{ background: '#f5f5f5', padding: '10px', borderBottom: '1px solid #ddd' }}>
                <strong>Subject:</strong> {preview.subject}
              </div>
              <pre style={{
                padding: '15px',
                whiteSpace: 'pre-wrap',
                fontFamily: 'monospace',
                fontSize: '0.85em',
                margin: 0,
                background: '#282c34',
                color: '#abb2bf',
                maxHeight: '600px',
                overflow: 'auto'
              }}>
                {preview.bodyHtml}
              </pre>
            </div>
          )}
        </Tab.Pane>
      )
    }
  ];

  return (
    <Container style={{ marginTop: '50px', maxWidth: '1200px' }}>
      <Button basic onClick={() => navigate('/email-templates')} style={{ marginBottom: '20px' }}>
        <Icon name='arrow left' /> Back to Email Templates
      </Button>

      <Header as='h1'>
        <Icon name='eye' />
        <Header.Content>
          Email Template Preview
          {template && (
            <Header.Subheader>
              {template.templateType} - {template.templateLevel}
              {template.brokerageName && ` - ${template.brokerageName}`}
              {template.providerName && ` - ${template.providerName}`}
              {template.policyTypeName && ` - ${template.policyTypeName}`}
            </Header.Subheader>
          )}
        </Header.Content>
      </Header>

      {error && (
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      <Grid stackable>
        <Grid.Row>
          <Grid.Column width={5}>
            <Segment>
              <Header as='h3'>
                <Icon name='settings' />
                Test Variables
              </Header>
              <Form>
                {Object.entries(testVariables).map(([key, value]) => (
                  <Form.Field key={key}>
                    <label style={{ textTransform: 'capitalize' }}>
                      {key.replaceAll(/([A-Z])/g, ' $1').trim()}
                    </label>
                    <Form.Input
                      value={value}
                      onChange={(e) => handleVariableChange(key, e.target.value)}
                      size='small'
                    />
                  </Form.Field>
                ))}
                <Button
                  primary
                  fluid
                  onClick={handleRefreshPreview}
                  loading={loading}
                  disabled={!id}
                >
                  <Icon name='refresh' />
                  Refresh Preview
                </Button>
              </Form>
            </Segment>

            {template && (
              <Segment>
                <Header as='h4'>
                  <Icon name='info circle' />
                  Template Info
                </Header>
                <div style={{ fontSize: '0.9em' }}>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Type:</strong> {template.templateType}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Level:</strong> {template.templateLevel}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Version:</strong> v{template.version || 1}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Status:</strong>{' '}
                    {template.isActive ? (
                      <span style={{ color: 'green' }}>Active</span>
                    ) : (
                      <span style={{ color: 'grey' }}>Inactive</span>
                    )}
                  </div>
                  {template.createdBy && (
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Created By:</strong> {template.createdBy}
                    </div>
                  )}
                </div>
              </Segment>
            )}
          </Grid.Column>

          <Grid.Column width={11}>
            <Segment loading={loading}>
              <Tab panes={panes} />
            </Segment>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Container>
  );
}

export default EmailTemplatePreviewPage;
